import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { LineChart, PieChart } from '@mui/x-charts';
import RefreshIcon from '@mui/icons-material/Refresh';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

const colors = ['#38bdf8', '#22c55e', '#a78bfa', '#f59e0b', '#ef4444', '#14b8a6', '#60a5fa', '#f472b6'];
// Keep the usual types in a stable order, then append any type returned by
// the API.  This is important for imported/legacy values such as
// "Classique + Gravure", which must remain visible in the per-store matrix
// instead of being silently omitted.  Trace is intentionally excluded from
// the effective mounting result and therefore never appears as a column.
const MOUNTING_TYPE_COLUMN_ORDER = [
  'Classique',
  'Easy-Digifit',
  'T.D.',
  'Percées',
  'V.S.',
  'Clip',
  'Classique + Gravure',
];

function formatRate(value) {
  const number = Number(value || 0);
  return `${number.toFixed(1).replace('.', ',')} %`;
}

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatMonthLabel(date) {
  const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function getMonthRange(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  return {
    period: `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}`,
    label: formatMonthLabel(firstDay),
    from: formatDateInput(firstDay),
    to: formatDateInput(lastDay),
  };
}

function defaultDateRange() {
  const today = new Date();
  return getMonthRange(today.getFullYear(), today.getMonth() - 1);
}

function getPeriodOptions() {
  const today = new Date();
  // Keep a generous history so older imported periods remain selectable while
  // keeping the control compact and predictable.
  return Array.from({ length: 36 }, (_item, index) =>
    getMonthRange(today.getFullYear(), today.getMonth() - index)
  );
}

function csvEscape(value) {
  const text = String(value ?? '');
  return /[;"\n\r]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

function downloadCsv(filename, rows) {
  const content = rows.map((row) => row.map(csvEscape).join(';')).join('\n');
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getStoreShortLabel(store) {
  return store.storeCode || store.storeName || 'Magasin';
}

function isEdoiStore(store) {
  return /^EDOI$/i.test(String(store?.storeCode || '').trim())
    || /^EDOI$/i.test(String(store?.storeName || '').trim());
}

// Keep the default store table ordered by the numeric part of the site code
// (AA 01, ODB 02, …) instead of by the full display label.
function compareStoreCodes(a, b) {
  const codeA = String(a?.storeCode || '').trim();
  const codeB = String(b?.storeCode || '').trim();
  const numberA = codeA.match(/\d+/);
  const numberB = codeB.match(/\d+/);

  if (numberA && numberB) {
    const numericDiff = Number(numberA[0]) - Number(numberB[0]);
    if (numericDiff) return numericDiff;
    const prefixA = codeA.replace(/\d+/g, '').trim();
    const prefixB = codeB.replace(/\d+/g, '').trim();
    const prefixDiff = prefixA.localeCompare(prefixB, 'fr', { sensitivity: 'base' });
    if (prefixDiff) return prefixDiff;
  } else if (numberA) {
    return -1;
  } else if (numberB) {
    return 1;
  }

  return (codeA || a?.storeName || '').localeCompare(codeB || b?.storeName || '', 'fr', {
    sensitivity: 'base',
  });
}

function formatPeriodLabel(period) {
  if (!period) return '';
  const [year, month] = String(period).split('-');
  const date = new Date(Number(year), Number(month || 1) - 1, 1);
  if (Number.isNaN(date.getTime())) return period;
  return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
}

function TopStoresBarChart({ data }) {
  const maxValue = Math.max(...data.map((store) => Number(store.total || 0)), 0);
  // Environ 5 graduations « rondes » (1, 2, 2,5 ou 5 × 10^n). Une graduation par
  // unité produisait plus de 1 000 étiquettes et lignes de grille.
  const rawStep = Math.max(maxValue, 1) / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalizedStep = rawStep / magnitude;
  const niceFactor = [1, 2, 2.5, 5, 10].find((factor) => normalizedStep <= factor);
  const step = Math.max(1, Math.round(niceFactor * magnitude));
  const yMax = Math.max(step, Math.ceil(maxValue / step) * step);
  const ticks = Array.from({ length: Math.round(yMax / step) + 1 }, (_item, index) => yMax - index * step);

  return (
    <Box sx={{ height: 360, display: 'grid', gridTemplateRows: '1fr auto', gap: 1.5 }}>
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: '42px 1fr',
          minHeight: 250,
        }}
      >
        <Box sx={{ position: 'relative', borderRight: '1px solid', borderColor: 'divider' }}>
          {ticks.map((tick) => (
            <Typography
              key={tick}
              variant="caption"
              color="text.secondary"
              sx={{
                position: 'absolute',
                right: 8,
                top: `${((yMax - tick) / yMax) * 100}%`,
                transform: 'translateY(-50%)',
                fontWeight: 800,
              }}
            >
              {tick}
            </Typography>
          ))}
        </Box>
        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: `repeat(${data.length}, minmax(64px, 1fr))`,
            alignItems: 'end',
            gap: 2,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {ticks.map((tick) => (
            <Box
              key={tick}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${((yMax - tick) / yMax) * 100}%`,
                borderTop: '1px solid',
                borderColor: 'divider',
                opacity: tick === 0 ? 0 : 0.8,
                pointerEvents: 'none',
              }}
            />
          ))}
          {data.map((store, index) => {
            const color = colors[index % colors.length];
            const value = Number(store.total || 0);
            return (
              <Box
                key={store.storeSiteId || store.storeName || index}
                title={`${store.label || getStoreShortLabel(store)} : ${value}`}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  minWidth: 64,
                  height: `${Math.max(2, (value / yMax) * 100)}%`,
                  minHeight: value ? 8 : 2,
                  borderRadius: '7px 7px 0 0',
                  bgcolor: color,
                  boxShadow: `0 10px 22px ${color}33`,
                }}
              />
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `42px repeat(${data.length}, minmax(64px, 1fr))`,
          gap: 2,
          px: 2,
          overflowX: 'auto',
        }}
      >
        <Box />
        {data.map((store, index) => {
          const color = colors[index % colors.length];
          return (
            <Stack
              key={store.storeSiteId || store.storeName || index}
              spacing={0.5}
              alignItems="center"
              sx={{ minWidth: 64, textAlign: 'center' }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: color }} />
              <Typography variant="caption" sx={{ fontWeight: 850, lineHeight: 1.15 }}>
                {getStoreShortLabel(store)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                {store.total || 0}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
}

function EmptyChart({ label = 'Aucune donnée.' }) {
  return (
    <Box sx={{ height: 280, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
      {label}
    </Box>
  );
}

export default function StatisticsPage() {
  const menuPage = useMenuPage('/statistiques');
  const initialRange = useMemo(defaultDateRange, []);
  const periodOptions = useMemo(getPeriodOptions, []);
  const [period, setPeriod] = useState(initialRange.period);
  const [range, setRange] = useState({ from: initialRange.from, to: initialRange.to });
  const [stats, setStats] = useState(null);
  const [storeMountingTypeFilter, setStoreMountingTypeFilter] = useState('');
  const [storeMatrixSort, setStoreMatrixSort] = useState({ key: 'label', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams(range).toString();
      setStats(await requestJson(`/statistics/mounting?${query}`));
    } catch (err) {
      setError(err.message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const mountingTypeData = useMemo(
    () =>
      (stats?.byMountingType || []).map((item, index) => ({
        id: item.type,
        label: item.type,
        value: item.count,
        color: colors[index % colors.length],
      })),
    [stats]
  );

  const timelineData = useMemo(() => {
    const periods = [...new Set((stats?.timeline || []).map((item) => item.period))].sort();
    return periods.map((period) => ({
      period,
      count: (stats?.timeline || [])
        .filter((item) => item.period === period)
        .reduce((sum, item) => sum + item.count, 0),
    }));
  }, [stats]);

  const storeBarData = useMemo(
    () =>
      (stats?.byStore || [])
        .map((store) => {
          if (!storeMountingTypeFilter) return store;
          const matchingType = (store.mountingTypes || []).find((item) => item.type === storeMountingTypeFilter);
          return {
            ...store,
            total: matchingType?.count || 0,
            mountingTypes: matchingType ? [matchingType] : [],
          };
        })
        .filter((store) => store.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10),
    [stats, storeMountingTypeFilter]
  );

  const mountingTypeColumns = useMemo(
    () => {
      const availableTypes = new Set(
        (stats?.byMountingType || []).map((item) => item.type).filter((type) => type !== 'Trace')
      );
      const orderedTypes = MOUNTING_TYPE_COLUMN_ORDER.filter((type) => availableTypes.has(type));
      const additionalTypes = [...availableTypes]
        .filter((type) => !MOUNTING_TYPE_COLUMN_ORDER.includes(type))
        .sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }));
      return [...orderedTypes, ...additionalTypes];
    },
    [stats]
  );

  const storeMountingMatrix = useMemo(
    () => {
      const ncaByStore = new Map(
        (stats?.nonConformities?.byStore || []).map((item) => [
          String(item.storeSiteId || item.storeName || ''),
          item.ncaCount || 0,
        ])
      );
      const ncmByStore = new Map(
        (stats?.nonConformities?.byStore || []).map((item) => [
          String(item.storeSiteId || item.storeName || ''),
          item.ncmCount || 0,
        ])
      );
      return (stats?.byStore || []).map((store) => {
        // The matrix represents effective workshop output: jobs flagged by a
        // traceability report, NCA or NCM are excluded from the mounting type
        // counts as well as from the final total.  Keep the raw count on the
        // API object for the other charts, but display the conforming count in
        // this table so the columns add up to the adjusted total.
        const counts = Object.fromEntries(
          (store.mountingTypes || []).map((item) => [item.type, item.conformingCount ?? item.count])
        );
        const adjustedTotal = Number(store.adjustedTotal ?? store.total ?? 0);
        const ncaCount = ncaByStore.get(String(store.storeSiteId || store.storeName || '')) || 0;
        const ncmCount = ncmByStore.get(String(store.storeSiteId || store.storeName || '')) || 0;
        const ncCount = ncaCount + ncmCount;
        return {
          ...store,
          label: [store.storeCode, store.storeName].filter(Boolean).join(' - ') || 'Magasin non renseigné',
          counts,
          adjustedTotal,
          ncaCount,
          ncmCount,
          ncCount,
          ncRate: store.total ? (ncCount / store.total) * 100 : 0,
        };
      });
    },
    [stats]
  );

  const storeMatrixSortOptions = useMemo(
    () => [
      { key: 'label', label: 'Magasin' },
      ...mountingTypeColumns.map((type) => ({ key: type, label: type })),
      { key: 'traceability', label: 'Traçabilité' },
      { key: 'nca', label: 'NCA' },
      { key: 'ncm', label: 'NCM' },
      { key: 'ncRate', label: '% NC' },
      { key: 'total', label: 'Total' },
    ],
    [mountingTypeColumns]
  );

  const sortedStoreMountingMatrix = useMemo(() => {
    const direction = storeMatrixSort.direction === 'asc' ? 1 : -1;
    return [...storeMountingMatrix].sort((a, b) => {
      // EDOI est volontairement conservé en dernière ligne, même lorsque
      // l'utilisateur trie le tableau sur un indicateur statistique.
      if (isEdoiStore(a) !== isEdoiStore(b)) return isEdoiStore(a) ? 1 : -1;
      if (storeMatrixSort.key === 'label') {
        return direction * compareStoreCodes(a, b);
      }
      const getValue = (store) => {
        if (storeMatrixSort.key === 'total') return Number(store.adjustedTotal ?? store.total ?? 0);
        if (storeMatrixSort.key === 'traceability') return Number(store.traceabilityCount || 0);
        if (storeMatrixSort.key === 'nca') return Number(store.ncaCount || 0);
        if (storeMatrixSort.key === 'ncm') return Number(store.ncmCount || 0);
        if (storeMatrixSort.key === 'ncRate') return Number(store.ncRate || 0);
        return Number(store.counts[storeMatrixSort.key] || 0);
      };
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue !== bValue) return direction * (aValue - bValue);
      return a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });
    });
  }, [storeMountingMatrix, storeMatrixSort]);

  const handleStoreMatrixSort = (key) => {
    setStoreMatrixSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleExportCsv = () => {
    const rows = [
      ['Magasin', ...mountingTypeColumns, 'Traçabilité', 'NCA', 'NCM', '% NC', 'Total'],
      ...sortedStoreMountingMatrix.map((store) => [
        store.label,
        ...mountingTypeColumns.map((type) => store.counts[type] || 0),
        store.traceabilityCount || 0,
        store.ncaCount || 0,
        store.ncmCount || 0,
        formatRate(store.ncRate || 0),
        store.adjustedTotal ?? store.total ?? 0,
      ]),
    ];
    downloadCsv('stats-montage-' + (range.from || 'debut') + '-' + (range.to || 'fin') + '.csv', rows);
  };

  const handlePeriodChange = (event) => {
    const nextPeriod = event.target.value;
    const selectedPeriod = periodOptions.find((option) => option.period === nextPeriod);
    if (!selectedPeriod) {
      setPeriod('');
      return;
    }
    setPeriod(selectedPeriod.period);
    setRange({ from: selectedPeriod.from, to: selectedPeriod.to });
  };

  const handleFromChange = (event) => {
    setPeriod('');
    setRange((current) => ({ ...current, from: event.target.value }));
  };

  const handleToChange = (event) => {
    setPeriod('');
    setRange((current) => ({ ...current, to: event.target.value }));
  };

  const topMounting = stats?.byMountingType?.[0];
  const topStore = stats?.byStore?.[0];

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {menuPage.pageTitle}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            select
            label="Période"
            value={period}
            onChange={handlePeriodChange}
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
            sx={{ minWidth: 170 }}
          >
            <option value="">Période personnalisée</option>
            {periodOptions.map((option) => (
              <option key={option.period} value={option.period}>
                {option.label}
              </option>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Du"
            value={range.from}
            onChange={handleFromChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Au"
            value={range.to}
            onChange={handleToChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStats}>
            Rafraîchir
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={handleExportCsv}
            disabled={!storeMountingMatrix.length}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <QueryStatsRoundedIcon color="primary" />
                <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
                  {stats?.adjustedTotal ?? stats?.total ?? 0}
                </Typography>
                <Typography color="text.secondary">Montages sur la période</Typography>
              </CardContent>
            </Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <PieChartRoundedIcon color="success" />
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  {topMounting?.type || '—'}
                </Typography>
                <Typography color="text.secondary">Type de montage dominant</Typography>
              </CardContent>
            </Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <StoreRoundedIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                  {[topStore?.storeCode, topStore?.storeName].filter(Boolean).join(' - ') || '—'}
                </Typography>
                <Typography color="text.secondary">Magasin le plus actif</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '0.9fr 1.1fr' }, gap: 2 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Répartition par type de montage
                </Typography>
                {mountingTypeData.length ? (
                  <PieChart
                    height={320}
                    colors={colors}
                    series={[
                      {
                        data: mountingTypeData,
                        innerRadius: 55,
                        outerRadius: 115,
                        paddingAngle: 3,
                        cornerRadius: 5,
                        highlightScope: { fade: 'global', highlight: 'item' },
                        faded: { innerRadius: 45, additionalRadius: -12, color: 'gray' },
                      },
                    ]}
                    slotProps={{ legend: { position: { vertical: 'middle', horizontal: 'right' } } }}
                  />
                ) : (
                  <EmptyChart />
                )}
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Évolution temporelle
                </Typography>
                {timelineData.length ? (
                  <LineChart
                    height={320}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: timelineData.map((item) => item.period),
                        valueFormatter: formatPeriodLabel,
                        tickInterval: timelineData.map((item) => item.period),
                        tickLabelInterval: () => true,
                        tickLabelMinGap: 0,
                        height: 58,
                        tickSize: 6,
                        tickLabelStyle: { fontSize: 12, fontWeight: 800, fill: 'currentColor' },
                      },
                    ]}
                    margin={{ bottom: 28 }}
                    yAxis={[{ min: 0 }]}
                    series={[
                      {
                        data: timelineData.map((item) => item.count),
                        label: 'Montages',
                        color: '#38bdf8',
                        area: true,
                        showMark: true,
                      },
                    ]}
                    grid={{ horizontal: true }}
                    sx={{
                      '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                        display: 'block',
                        fill: 'currentColor',
                        opacity: 1,
                      },
                    }}
                  />
                ) : (
                  <EmptyChart label="Aucune donnée temporelle." />
                )}
              </CardContent>
            </Card>
          </Box>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Top magasins
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {storeMountingTypeFilter
                      ? `Filtré sur ${storeMountingTypeFilter}`
                      : 'Tous types de montage confondus'}
                  </Typography>
                </Box>
                <TextField
                  select
                  SelectProps={{ native: true }}
                  size="small"
                  value={storeMountingTypeFilter}
                  onChange={(event) => setStoreMountingTypeFilter(event.target.value)}
                  inputProps={{ 'aria-label': 'Filtrer par type de montage' }}
                  sx={{ minWidth: { md: 260 }, '& select': { py: 1.15 } }}
                >
                  <option value="">Tous les types</option>
                  {(stats?.byMountingType || []).map((item) => (
                    <option key={item.type} value={item.type}>
                      {item.type}
                    </option>
                  ))}
                </TextField>
              </Stack>
              {storeBarData.length ? (
                <TopStoresBarChart data={storeBarData} />
              ) : (
                <EmptyChart label="Aucun magasin sur la période." />
              )}
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Répartition par magasin
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <TextField
                    label="Trier par"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={storeMatrixSort.key}
                    onChange={(event) => setStoreMatrixSort((current) => ({ ...current, key: event.target.value }))}
                    sx={{ minWidth: { sm: 180 } }}
                  >
                    {storeMatrixSortOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    label="Ordre"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={storeMatrixSort.direction}
                    onChange={(event) => setStoreMatrixSort((current) => ({ ...current, direction: event.target.value }))}
                    sx={{ minWidth: { sm: 150 } }}
                  >
                    <option value="desc">Plus grand</option>
                    <option value="asc">Plus petit</option>
                  </TextField>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadRoundedIcon />}
                    onClick={handleExportCsv}
                    disabled={!storeMountingMatrix.length}
                    sx={{ minHeight: 40 }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableSortLabel-root': { fontWeight: 900 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.45 },
                    '& .Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={storeMatrixSort.key === 'label' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'label'}
                          direction={storeMatrixSort.key === 'label' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('label')}
                        >
                          Magasin
                        </TableSortLabel>
                      </TableCell>
                      {mountingTypeColumns.map((type) => (
                        <TableCell key={type} align="right" sortDirection={storeMatrixSort.key === type ? storeMatrixSort.direction : false}>
                          <TableSortLabel
                            active={storeMatrixSort.key === type}
                            direction={storeMatrixSort.key === type ? storeMatrixSort.direction : 'asc'}
                            onClick={() => handleStoreMatrixSort(type)}
                          >
                            {type}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'traceability' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'traceability'}
                          direction={storeMatrixSort.key === 'traceability' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('traceability')}
                        >
                          Traçabilité
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'nca' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'nca'}
                          direction={storeMatrixSort.key === 'nca' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('nca')}
                        >
                          NCA
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'ncm' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'ncm'}
                          direction={storeMatrixSort.key === 'ncm' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('ncm')}
                        >
                          NCM
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'ncRate' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'ncRate'}
                          direction={storeMatrixSort.key === 'ncRate' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('ncRate')}
                        >
                          % NC
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={storeMatrixSort.key === 'total' ? storeMatrixSort.direction : false}>
                        <TableSortLabel
                          active={storeMatrixSort.key === 'total'}
                          direction={storeMatrixSort.key === 'total' ? storeMatrixSort.direction : 'asc'}
                          onClick={() => handleStoreMatrixSort('total')}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStoreMountingMatrix.map((store) => (
                      <TableRow key={store.storeSiteId || store.storeName}>
                        <TableCell sx={{ fontWeight: 800 }}>{store.label}</TableCell>
                        {mountingTypeColumns.map((type) => (
                          <TableCell key={type} align="right" sx={{ fontWeight: store.counts[type] ? 850 : 400 }}>
                            {store.counts[type] || 0}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: store.traceabilityCount ? 850 : 400 }}>
                          {store.traceabilityCount || 0}
                        </TableCell>
                        <TableCell align="right">
                          {store.ncaCount ? (
                            <Chip size="small" color="warning" variant="outlined" label={store.ncaCount} sx={{ minWidth: 34, fontWeight: 950 }} />
                          ) : (
                            0
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {store.ncmCount ? (
                            <Chip size="small" color="warning" variant="outlined" label={store.ncmCount} sx={{ minWidth: 34, fontWeight: 950 }} />
                          ) : (
                            0
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            color={store.ncRate ? 'warning' : 'default'}
                            variant="outlined"
                            label={formatRate(store.ncRate)}
                            sx={{ minWidth: 58, fontWeight: 950 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={store.adjustedTotal ?? store.total ?? 0}
                            sx={{
                              minWidth: 38,
                              bgcolor: 'rgba(245, 158, 11, 0.18)',
                              border: '1px solid rgba(245, 158, 11, 0.48)',
                              color: '#fbbf24',
                              fontWeight: 950,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
